
import { supabase } from '@/integrations/supabase/client';
import { Task, TaskStatus } from './types';
import { mapSupabaseTaskToTask, mapTaskToSupabaseTask, sortTasksByStatus } from './taskUtils';

export const fetchTasksFromSupabase = async (userId: string, listId: string): Promise<Task[]> => {
  const { data, error } = await supabase
    .from('tasks')
    .select('*')
    .eq('user_id', userId)
    .eq('list_id', listId)
    .order('position', { ascending: true });
    
  if (error) throw error;
  
  const mappedTasks: Task[] = (data || []).map((task, index) => 
    mapSupabaseTaskToTask(task, index)
  );
  
  return sortTasksByStatus(mappedTasks);
};

export const fetchAllTasksFromSupabase = async (userId: string): Promise<Task[]> => {
  const { data, error } = await supabase
    .from('tasks')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
    
  if (error) throw error;
  
  return (data || []).map(task => mapSupabaseTaskToTask(task));
};

export const insertTaskToSupabase = async (task: Task, userId: string): Promise<void> => {
  const supabaseTask = mapTaskToSupabaseTask(task, userId);
  
  const { error } = await supabase
    .from('tasks')
    .insert(supabaseTask);
    
  if (error) {
    console.error('Error adding task to Supabase:', error);
    throw error;
  }
};

export const updateTaskInSupabase = async (task: Task, userId: string): Promise<void> => {
  const supabaseTask = {
    title: task.title,
    detail: task.detail,
    status: task.status,
    list_id: task.list_id,
    total_time_spent: task.totalTimeSpent,
    pomodoros_completed: task.pomodorosCompleted,
    position: task.position,
    priority_quadrant: task.priority_quadrant,
    priority_x: task.priority_x,
    priority_y: task.priority_y
  };
  
  const { error } = await supabase
    .from('tasks')
    .update(supabaseTask)
    .eq('id', task.id)
    .eq('user_id', userId);
    
  if (error) {
    console.error('Error updating task in Supabase:', error);
    throw error;
  }
};

export const deleteTaskFromSupabase = async (taskId: string, userId: string): Promise<void> => {
  const { error } = await supabase
    .from('tasks')
    .delete()
    .eq('id', taskId)
    .eq('user_id', userId);
    
  if (error) {
    console.error('Error deleting task from Supabase:', error);
    throw error;
  }
};

export const updateTaskPositionsInSupabase = async (tasks: Task[], userId: string): Promise<void> => {
  const updates = tasks.map(task => 
    supabase
      .from('tasks')
      .update({ position: task.position })
      .eq('id', task.id)
      .eq('user_id', userId)
  );
  
  await Promise.all(updates);
};
